import { globalFetchPolicy } from "@/components/layout/fetchPolicies"
import { useCustomLogoutMutation, useMeLazyQuery, useRefreshAccessTokenMutation } from "@/graphql"
import { AUTH_TOKEN_CHANGED_EVENT, USER_TOKEN, getAuthToken, removeAuthToken, setAuthToken } from "@/helpers/auth"
import { AuthTelemetryReason, trackAuthTelemetry } from "@/helpers/authTelemetry"
import { createdAppDataLocalStore, createdProjectId } from "@/helpers/globalVariables"
import { pageHydrated } from "@/helpers/pageHydrated"
import {
  classifyUnrecoverableRefresh,
  getRefreshMutationFailure,
  isRecoverableRefreshError,
  type SessionEndReason,
} from "@/pages/auth/helpers/refreshErrorClassifiers"
import { RoleTypes } from "@/types"
import { message } from "antd"
import { JwtPayload, jwtDecode } from "jwt-decode"
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useParams } from "react-router"

type AuthContextProps = {
  role: RoleTypes
  user: MeQuery["me"]
  isOutgoingOnly: boolean
  organization: Maybe<string>
  logout: () => Promise<void>
  getUser: () => Promise<MeQuery["me"]>
  setUser: (user: MeQuery["me"] | null) => void
  setRole: (role: RoleTypes | null) => void
  isUserDataLoading: boolean
  hasCheckedAuth: boolean
}

type AuthProviderProps = PropsWithChildren<Partial<AuthContextProps>>

/**
 * Outcome of a token-refresh attempt:
 * - "success": a new access token was issued.
 * - "recoverable": transient network/offline/server problem — keep the session and retry later.
 * - "unrecoverable": the server rejected the refresh token — must re-login (carries the reason).
 */
type RefreshResult =
  | { outcome: "success" }
  | { outcome: "recoverable" }
  | { outcome: "unrecoverable"; endReason: SessionEndReason }

/** Delay before retrying after a transient (network/offline) refresh failure. */
const RECOVERABLE_RETRY_DELAY_MS = 30_000

/** Proactive refresh lead time — must be shorter than the access JWT lifetime or short-TTL sessions never expire. */
const PROACTIVE_REFRESH_BEFORE_MS = 5 * 60 * 1000

/** Treat refresh-cookie deadlines within this window as a short session (dev idle-logout testing). */
const SHORT_REFRESH_SESSION_MS = 15 * 60 * 1000

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

/** After sleep, the browser can report offline until Wi‑Fi is ready; avoids burning all refresh retries. */
function waitForOnline(maxMs: number): Promise<void> {
  if (typeof window === "undefined" || typeof navigator === "undefined" || navigator.onLine) {
    return Promise.resolve()
  }
  return new Promise(resolve => {
    let settled = false
    const finish = () => {
      if (settled) {
        return
      }
      settled = true
      window.removeEventListener("online", onOnline)
      window.clearTimeout(timeoutId)
      resolve()
    }
    const onOnline = () => finish()
    window.addEventListener("online", onOnline)
    const timeoutId = window.setTimeout(finish, maxMs)
  })
}

// export function useAuth(): AuthContextProps {
//   const context = useContext(AuthContext)
//   if (context === undefined) {
//     throw new Error('useAuth must be used within an AuthProvider')
//   }
//   return context
// }
export function useAuth(): AuthContextProps {
  const context = useContext(AuthContext)

  // Instead of throwing an error, return safe defaults
  if (!context) {
    return {
      logout(): Promise<void> {
        return Promise.resolve(undefined)
      },
      setUser(): void {
        return
      },
      user: null,
      isOutgoingOnly: false,
      role: null,
      organization: null,
      setRole: () => null,
      getUser: async () => null,
      isUserDataLoading: true,
      hasCheckedAuth: false,
    }
  }

  return context
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<MeQuery["me"] | null>(null)
  const [role, setRole] = useState<RoleTypes>(null)
  const [isOutgoingOnly, setIsOutgoingOnly] = useState(false)
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false)
  const [authToken, setAuthTokenState] = useState<string | null>(() => getAuthToken())
  const refreshPromiseRef = useRef<Promise<RefreshResult> | null>(null)
  const sessionExpiredHandledRef = useRef(false)
  /** Prevents overlapping "me failed → refresh" recovery runs. */
  const meAuthRecoverRef = useRef(false)
  /** Pending "retry after a transient/network refresh failure" timer. */
  const recoverableRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Latest outcome handler, so the recoverable-retry timer never calls a stale closure. */
  const runRefreshAndHandleRef = useRef<(source: string) => Promise<void>>(() => Promise.resolve())
  const { org } = useParams()

  const [getMe, { data: me, error, loading: isUserDataLoading }] = useMeLazyQuery({ fetchPolicy: globalFetchPolicy })

  const [refreshAccessTokenMutation] = useRefreshAccessTokenMutation()
  const [customLogoutMutation] = useCustomLogoutMutation()
  const REFRESH_TOKEN_EXPIRES_AT_KEY = "refreshTokenExpiresAt"

  const safeStorageRemoveItem = useCallback((key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch {
      // Ignore storage restrictions.
    }
  }, [])

  const safeStorageGetItem = useCallback((key: string): string | null => {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }, [])

  const isUnauthorizedError = useCallback((apolloError: unknown): boolean => {
    if (!apolloError || typeof apolloError !== "object") {
      return false
    }

    const maybeError = apolloError as {
      graphQLErrors?: Array<{ message?: string; extensions?: { code?: string } }>
      networkError?: { statusCode?: number; response?: { status?: number } } & Record<string, unknown>
      message?: string
    }

    const graphQLErrorUnauthorized = (maybeError.graphQLErrors ?? []).some(err => {
      const code = err?.extensions?.code
      const messageText = err?.message?.toLowerCase() ?? ""
      return code === "UNAUTHENTICATED" || messageText.includes("unauthorized")
    })

    if (graphQLErrorUnauthorized) {
      return true
    }

    const msg = (maybeError.message ?? "").toLowerCase()
    if (msg.includes("unauthorized")) {
      return true
    }
    // HttpLink: whole GraphQL POST returns 401 JSON — often only `message`, not `networkError.statusCode`.
    if (msg.includes("status code 401") || msg.includes("status code: 401")) {
      return true
    }

    const ne = maybeError.networkError as
      | { statusCode?: number; status?: number; response?: { status?: number } }
      | undefined
    const statusCode = ne?.statusCode ?? ne?.status ?? ne?.response?.status
    if (statusCode === 401) {
      return true
    }

    return false
  }, [])

  const organization = me?.me?.organization?.data?.attributes?.slug ?? org

  const setAccessToken = useCallback((token: string) => {
    setAuthToken(token)
    setAuthTokenState(token)
  }, [])

  const clearAccessToken = useCallback(() => {
    removeAuthToken()
    setAuthTokenState(null)
  }, [])

  /** Deduped refresh with widened retries/backoff (covers flaky network, laptop sleep, redeploys). */
  const refreshAccessTokenWithRetry = useCallback(async (): Promise<RefreshResult> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    const ATTEMPTS = 8
    const BASE_PAUSE_MS = 2000
    const MAX_PAUSE_MS = 30_000

    refreshPromiseRef.current = (async () => {
      await waitForOnline(30_000)
      for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await waitForOnline(15_000)
        }
        try {
          const result = await refreshAccessTokenMutation({
            variables: { input: {} },
            errorPolicy: "none",
          })
          if (result.data?.refreshAccessToken?.jwt) {
            setAccessToken(result.data.refreshAccessToken.jwt)
            void getMe({ fetchPolicy: "network-only" })
            return { outcome: "success" }
          }
          const mutationFailure = getRefreshMutationFailure(result)
          if (mutationFailure) {
            return mutationFailure
          }
        } catch (error) {
          if (!isRecoverableRefreshError(error)) {
            // Refresh token rejected by the server → user must re-authenticate.
            return { outcome: "unrecoverable", endReason: classifyUnrecoverableRefresh(error) }
          }
          // Transient failure — fall through to backoff and retry.
        }
        if (attempt < ATTEMPTS - 1) {
          const pauseMs = Math.min(BASE_PAUSE_MS * 2 ** attempt, MAX_PAUSE_MS)
          await new Promise(r => setTimeout(r, pauseMs))
        }
      }
      // Exhausted retries without a definitive rejection → keep the session, retry later.
      return { outcome: "recoverable" }
    })()

    try {
      return await refreshPromiseRef.current
    } finally {
      refreshPromiseRef.current = null
    }
  }, [getMe, refreshAccessTokenMutation, setAccessToken])

  const handleSessionExpired = useCallback(
    (reason: AuthTelemetryReason, details?: Record<string, unknown>, endReason: SessionEndReason = "generic") => {
      if (sessionExpiredHandledRef.current) {
        return
      }
      sessionExpiredHandledRef.current = true
      trackAuthTelemetry({
        reason,
        source: "AuthProvider.handleSessionExpired",
        details: { ...details, endReason },
      })
      clearAccessToken()
      safeStorageRemoveItem(REFRESH_TOKEN_EXPIRES_AT_KEY)
      if (endReason === "payment_overdue") {
        message.warning(
          "You have been signed out: your organization has an outstanding account with Easetech. " +
            "Please contact support@easetech.co.za to settle it.",
          10
        )
      } else {
        const notice =
          endReason === "revoked_by_other_login"
            ? "You have been signed out because your account was signed in on another device or browser."
            : "Your session has expired. Please login again."
        message.info(notice)
      }
      setUser(null)
    },
    [clearAccessToken, safeStorageRemoveItem]
  )

  const clearRecoverableRetry = useCallback(() => {
    if (recoverableRetryRef.current) {
      clearTimeout(recoverableRetryRef.current)
      recoverableRetryRef.current = null
    }
  }, [])

  /**
   * Runs a refresh and reacts to the outcome:
   * - "unrecoverable" → end the session.
   * - "recoverable" → keep the session and schedule another attempt (no logout).
   * - "success" → the access token changed; the proactive effect reschedules the next refresh.
   */
  const runRefreshAndHandle = useCallback(
    async (source: string): Promise<void> => {
      const result = await refreshAccessTokenWithRetry()
      if (result.outcome === "unrecoverable") {
        clearRecoverableRetry()
        handleSessionExpired("refresh_failed", { source }, result.endReason)
        return
      }
      if (result.outcome === "recoverable") {
        clearRecoverableRetry()
        recoverableRetryRef.current = setTimeout(() => {
          recoverableRetryRef.current = null
          void runRefreshAndHandleRef.current(source)
        }, RECOVERABLE_RETRY_DELAY_MS)
      }
    },
    [refreshAccessTokenWithRetry, handleSessionExpired, clearRecoverableRetry]
  )
  runRefreshAndHandleRef.current = runRefreshAndHandle

  useEffect(() => {
    if (isUserDataLoading) {
      return
    }
    if (!error) {
      meAuthRecoverRef.current = false
      setUser(me?.me ?? null)
      setRole(me?.me?.role?.data?.attributes?.name.toLowerCase() as RoleTypes)
      setIsOutgoingOnly(me?.me?.organization?.data?.attributes?.processingType === "outgoing")
      return
    }
    if (isUnauthorizedError(error) && getAuthToken()) {
      if (meAuthRecoverRef.current) {
        return
      }
      meAuthRecoverRef.current = true
      // Recoverable (network) failures keep the session and retry; only a server-side
      // rejection of the refresh token ends the session.
      void runRefreshAndHandle("me-unauthorized")
      return
    }
    // Do not wipe session on transient `me` failures while an access token still exists
    // (e.g. HTTP 401 shape Apollo does not classify — DashboardLayout avoids a login redirect).
    if (getAuthToken()) {
      return
    }
    setUser(null)
    setRole(undefined)
  }, [error, isUnauthorizedError, isUserDataLoading, me, runRefreshAndHandle])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === USER_TOKEN) {
        setAuthTokenState(event.newValue)
        // Tabs share one session: another tab signed in (possibly to another organization),
        // rotated the token or signed out. Reload `me` so this tab never shows one user while
        // sending another user's token — and so org-mismatch checks see the real session.
        if (event.newValue) {
          void getMe({ fetchPolicy: "network-only" })
        }
      }
    }

    const onTokenChanged = () => {
      setAuthTokenState(getAuthToken())
    }

    window.addEventListener("storage", onStorage)
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged)
    return () => {
      window.removeEventListener("storage", onStorage)
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged)
    }
  }, [getMe])

  useEffect(() => {
    sessionExpiredHandledRef.current = false
    meAuthRecoverRef.current = false
  }, [authToken])

  /**
   * Proactive refresh from access JWT `exp` (server refresh cookie + mutation). No mirror of refresh
   * expiry in localStorage timers beyond this.
   *
   * Short-lived access tokens (e.g. 5m session testing) must refresh only at expiry — refreshing
   * "5 min before expiry" would fire immediately on every rotation and slide the refresh token forever.
   */
  useEffect(() => {
    if (!authToken) {
      setUser(null)
      safeStorageRemoveItem(REFRESH_TOKEN_EXPIRES_AT_KEY)
      clearRecoverableRetry()
      return
    }

    // A fresh access token supersedes any pending recoverable-retry timer.
    clearRecoverableRetry()

    let expMs: number | null = null
    try {
      const payload = jwtDecode<JwtPayload>(authToken)
      if (payload.exp) {
        expMs = payload.exp * 1000
      }
    } catch {
      expMs = null
    }

    if (expMs === null) {
      void runRefreshAndHandle("access-token-jwt-decode")
      return
    }

    const expiresInMs = expMs - Date.now()
    const disableProactiveRefresh =
      import.meta.env.WEBSITE_AUTH_DISABLE_PROACTIVE_REFRESH === "true" ||
      import.meta.env.WEBSITE_AUTH_DISABLE_PROACTIVE_REFRESH === "1"
    const shortLivedAccessToken = expiresInMs <= PROACTIVE_REFRESH_BEFORE_MS
    const refreshAtExpiryOnly = disableProactiveRefresh || shortLivedAccessToken

    if (import.meta.env.DEV && shortLivedAccessToken) {
      console.info("[auth] Short-lived access token — refresh scheduled at expiry only (idle logout test).")
    }

    if (refreshAtExpiryOnly) {
      if (expiresInMs <= 0) {
        void runRefreshAndHandle("access-token-expired")
        return
      }
      const timeout = setTimeout(() => void runRefreshAndHandle("access-token-expired"), expiresInMs)
      return () => clearTimeout(timeout)
    }

    if (expiresInMs <= PROACTIVE_REFRESH_BEFORE_MS) {
      void runRefreshAndHandle("access-token-refresh")
      return
    }

    const timeout = setTimeout(
      () => void runRefreshAndHandle("access-token-refresh"),
      expiresInMs - PROACTIVE_REFRESH_BEFORE_MS
    )
    return () => clearTimeout(timeout)
  }, [authToken, runRefreshAndHandle, clearRecoverableRetry, safeStorageRemoveItem])

  /**
   * Backup idle logout for short refresh sessions: login stores `refreshTokenExpiresAt` from the CMS.
   * Used when access-token timers alone are not enough (e.g. proactive refresh was still extending).
   */
  useEffect(() => {
    if (!authToken) {
      return
    }

    const storedExpiry = safeStorageGetItem(REFRESH_TOKEN_EXPIRES_AT_KEY)
    if (!storedExpiry) {
      return
    }

    const expiresAtMs = new Date(storedExpiry).getTime()
    if (!Number.isFinite(expiresAtMs)) {
      return
    }

    const remaining = expiresAtMs - Date.now()
    const disableProactiveRefresh =
      import.meta.env.WEBSITE_AUTH_DISABLE_PROACTIVE_REFRESH === "true" ||
      import.meta.env.WEBSITE_AUTH_DISABLE_PROACTIVE_REFRESH === "1"

    if (remaining > SHORT_REFRESH_SESSION_MS && !disableProactiveRefresh) {
      return
    }

    if (import.meta.env.DEV) {
      console.info(`[auth] Refresh cookie idle logout in ${Math.max(0, Math.round(remaining / 1000))}s`)
    }

    if (remaining <= 0) {
      void runRefreshAndHandle("refresh-token-expired-storage")
      return
    }

    const timeout = setTimeout(() => void runRefreshAndHandle("refresh-token-expired-storage"), remaining)
    return () => clearTimeout(timeout)
  }, [authToken, runRefreshAndHandle, safeStorageGetItem])

  useEffect(() => () => clearRecoverableRetry(), [clearRecoverableRetry])

  useEffect(() => {
    // On a prerendered page, after React has taken the page over: the query's loading state would otherwise reach the
    // page before it is hydrated and make React draw it again.
    void pageHydrated
      .then(() => getMe())
      .finally(() => {
        setHasCheckedAuth(true)
      })
  }, [getMe])

  useEffect(() => {
    if (!error) {
      return
    }

    if (isUnauthorizedError(error)) {
      trackAuthTelemetry({
        reason: "unauthorized_response",
        source: "AuthProvider.useMeLazyQuery.error",
        message: error.message,
      })
    }
  }, [error, isUnauthorizedError])

  const logout = useCallback(async () => {
    trackAuthTelemetry({
      reason: "manual_logout",
      source: "AuthProvider.logout",
    })

    // Revoke refresh token on server (removes from database and clears cookie)
    try {
      await customLogoutMutation()
    } catch (error) {
      // Continue with logout even if mutation fails (e.g., network error)
      console.error("Logout mutation failed:", error)
    }

    // Clear local state
    clearAccessToken()
    safeStorageRemoveItem(USER_TOKEN)
    safeStorageRemoveItem(REFRESH_TOKEN_EXPIRES_AT_KEY)
    safeStorageRemoveItem(createdAppDataLocalStore)
    safeStorageRemoveItem(createdProjectId)
    await getMe({ context: { headers: { authorization: undefined } } })
    setUser(null)
  }, [customLogoutMutation, clearAccessToken, safeStorageRemoveItem, getMe])

  const getUser = useCallback(async () => {
    const { data } = await getMe()
    return data?.me
  }, [getMe])

  const contextValue = useMemo<AuthContextProps>(
    () => ({
      role,
      user,
      isOutgoingOnly,
      organization,
      setUser,
      setRole,
      logout,
      getUser,
      isUserDataLoading,
      hasCheckedAuth,
    }),
    [role, user, isOutgoingOnly, organization, logout, getUser, isUserDataLoading, hasCheckedAuth]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
